import { useEffect, useRef } from 'react';
import { ScreenCapture } from '../ScreenCapture';
import { screenshotStorage } from '../services/screenshotStorage';
import { useAppDispatch, useAppSelector } from '../store';
import { isReady } from '../store/bootstrapSlice';
import {
  getFullPageScreenshotId,
  setFullPageScreenshotId,
} from '../store/uiSlice';
import { getLoggedIn } from '../store/userSlice';

export const useInitialScreenshot = () => {
  const dispatch = useAppDispatch();
  const ready = useAppSelector(isReady);
  const loggedIn = useAppSelector(getLoggedIn);
  const existingScreenshotId = useAppSelector(getFullPageScreenshotId);
  const screenshotCaptureRef = useRef<ScreenCapture | null>(null);
  const hasCapturedRef = useRef(false);

  useEffect(() => {
    // Only capture if app is ready, user is logged in, and we haven't captured yet
    if (
      ready === 'success' &&
      loggedIn &&
      !existingScreenshotId &&
      !hasCapturedRef.current
    ) {
      const captureFullPageScreenshot = async () => {
        try {
          // Initialize ScreenCapture
          if (!screenshotCaptureRef.current) {
            screenshotCaptureRef.current = new ScreenCapture();
          }

          // Capture full page screenshot using snapdom
          const screenshot = await screenshotCaptureRef.current.capture({
            quality: 0.8,
            preserveElements: true,
            useSnapdom: true,
            fullPage: true,
          });

          if (screenshot) {
            // Generate a unique ID for the screenshot
            const screenshotId = `fullPage_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            // Store the screenshot in IndexedDB
            await screenshotStorage.storeScreenshot(
              screenshotId,
              screenshot,
              'fullPage'
            );

            // Store only the ID in Redux
            dispatch(setFullPageScreenshotId(screenshotId));
            hasCapturedRef.current = true;
          }
        } catch (error) {
          console.error('Error capturing initial full page screenshot:', error);
        } finally {
          // Clean up ScreenCapture instance
          if (screenshotCaptureRef.current) {
            screenshotCaptureRef.current.destroy();
            screenshotCaptureRef.current = null;
          }
        }
      };

      let didCapture = false;
      const maybeCapture = () => {
        if (!didCapture) {
          didCapture = true;
          captureFullPageScreenshot();
        }
      };
      if (document.readyState === 'complete') {
        // Page already loaded
        maybeCapture();
      } else {
        window.addEventListener('load', maybeCapture);
      }
      return () => {
        window.removeEventListener('load', maybeCapture);
        if (screenshotCaptureRef.current) {
          screenshotCaptureRef.current.destroy();
          screenshotCaptureRef.current = null;
        }
      };
    }
  }, [ready, loggedIn, existingScreenshotId, dispatch]);

  return {
    fullPageScreenshotId: existingScreenshotId,
    hasCaptured: hasCapturedRef.current,
  };
};
